import { NavLink } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTrainingModeStore } from '@/store/trainingMode';
import { toast } from 'sonner';
import { RailUserMenu } from './RailUserMenu';
import type { LsNavGroup } from './navConfig';

interface RailProps {
  groups: LsNavGroup[];
  activeGroupId: string;
  subNavCollapsed: boolean;
  onToggleSubNav: () => void;
}

/**
 * The slim vertical icon rail. One icon per top-level section; selecting one
 * routes to its landing page and swaps the contextual sub-nav column.
 */
export function Rail({ groups, activeGroupId, subNavCollapsed, onToggleSubNav }: RailProps) {
  const { isTrainingMode } = useTrainingModeStore();

  return (
    <aside className="flex h-full w-14 shrink-0 flex-col items-center border-r border-border bg-[hsl(var(--ls-rail))] py-3">
      <NavLink to="/dashboard" className="mb-3 flex h-8 w-8 items-center justify-center" aria-label="Home">
        <img src="/blazepos-logo.png" alt="BlazePOS" className="h-7 w-7 object-contain" />
      </NavLink>

      <nav className="flex flex-1 flex-col items-center gap-1" aria-label="Primary">
        {groups.map((group) => {
          const Icon = group.icon;
          const active = group.id === activeGroupId;
          const blocked = isTrainingMode && group.id !== 'sell';

          return (
            <Tooltip key={group.id} delayDuration={0}>
              <TooltipTrigger asChild>
                <NavLink
                  to={group.to}
                  onClick={(e) => {
                    if (blocked) {
                      e.preventDefault();
                      toast.error('Navigation Locked', {
                        description: 'Exit Training Mode to navigate to other pages.',
                      });
                    }
                  }}
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                    active
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    blocked && 'opacity-40 cursor-not-allowed'
                  )}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon className="h-5 w-5" />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                {group.label}{blocked ? ' (locked)' : ''}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </nav>

      <div className="mt-2 flex flex-col items-center gap-1">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-lg text-muted-foreground hover:text-foreground"
              onClick={onToggleSubNav}
              aria-label={subNavCollapsed ? 'Expand menu' : 'Collapse menu'}
            >
              {subNavCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            {subNavCollapsed ? 'Expand menu' : 'Collapse menu'}
          </TooltipContent>
        </Tooltip>
        <RailUserMenu />
      </div>
    </aside>
  );
}
